import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Activity, UserPlus, PartyPopper, PenLine, MessageSquare, Send, HandHelping, Link2, Mail, MonitorSmartphone, CalendarCheck } from "lucide-react";

const POLL_MS = 45000;

// Admin "Launch health" pulse: rolling 24h counts (7d beneath) for the F&F
// launch funnel. First paint uses the server's 60s cache; polls bypass it so
// the panel can sit on a screen and stay live. Zeros render dimmed — it will
// be mostly zeros until the launch starts.
export const LaunchHealthPanel = ({ onFeedbackClick }) => {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.adminLaunchHealth().then(setD).catch(() => {});
    const t = setInterval(() => api.adminLaunchHealth(true).then(setD).catch(() => {}), POLL_MS);
    return () => clearInterval(t);
  }, []);

  if (!d) return null;

  const tiles = [
    { id: "signups", label: "Signups", icon: UserPlus, day: d.signups.day, week: d.signups.week, to: "/admin?tab=analytics" },
    { id: "online", label: "Online now", icon: Activity, day: d.online_now, week: null, to: "/admin?tab=analytics", live: true },
    { id: "celebrations", label: "Celebrations", icon: PartyPopper, day: d.celebrations.day, week: d.celebrations.week },
    { id: "stories", label: "Stories", icon: PenLine, day: d.stories.day, week: d.stories.week, to: "/stories" },
    { id: "feedback", label: "Feedback", icon: MessageSquare, day: d.feedback.day, week: d.feedback.week, badge: d.feedback.new_total, onClick: onFeedbackClick },
    { id: "applications", label: "Applications", icon: Send, day: d.applications.day, week: d.applications.week, to: "/admin?tab=analytics" },
    { id: "help", label: "Help requests", icon: HandHelping, day: d.help_requests.day, week: d.help_requests.week, to: "/opportunities?type=help" },
    // Referral-loop conversion: 24h/7d = referred friends who completed
    // onboarding; note line = all-time invites sent → friends joined.
    { id: "invites", label: "Invites → joined", icon: Link2, day: d.invites?.day ?? 0, week: d.invites?.week ?? null,
      note: `${d.invites?.shares_total ?? 0} sent → ${d.invites?.joined_total ?? 0} joined all-time` },
    // Coming-soon interest list — near-zero on preview (gate is host-exempt
    // here); meaningful once the prod holding page is live.
    { id: "interest", label: "Interest signups", icon: Mail, day: d.interest?.day ?? 0, week: d.interest?.week ?? null,
      note: d.interest?.total ? `${d.interest.total} on the list all-time` : "fills once the prod holding page is live" },
    // PWA installs — anonymous `appinstalled` beacon; counting starts Feb
    // 2026, iOS installs never fire the event (honest undercount).
    { id: "pwa", label: "PWA installs", icon: MonitorSmartphone, day: d.pwa_installs?.day ?? 0, week: d.pwa_installs?.week ?? null,
      note: d.pwa_installs?.total ? `${d.pwa_installs.total} all-time · counting since Feb 2026` : "counting starts now · iOS installs invisible" },
    // Calendar subscribers — big number = feeds actually FETCHED by a
    // calendar app in the last 7d (not merely token minted).
    { id: "calendar", label: "Calendar subs", icon: CalendarCheck, day: d.calendar_feed?.week ?? 0, week: null,
      sub: "feeds fetched · last 7d",
      note: d.calendar_feed?.ever_total ? `${d.calendar_feed.ever_total} ever subscribed all-time` : "no feed fetched yet" },
  ];
  const allQuiet = tiles.every((t) => !t.day && !t.badge);

  const Tile = ({ t }) => {
    const inner = (
      <>
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">
          <t.icon className="w-3 h-3" /> {t.label}
          {t.live && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
          {t.badge > 0 && (
            <span data-testid="launch-health-feedback-new" className="ml-auto rounded-full bg-red-100 text-red-700 px-1.5 text-[10px] font-bold">{t.badge} new</span>
          )}
        </div>
        <div className={`mt-1 text-2xl font-display tracking-tight ${t.day ? "text-forest" : "text-neutral-300"}`}>{t.day}</div>
        {t.sub != null ? (
          <div className="text-[10px] text-neutral-400">{t.sub}</div>
        ) : (
          t.week != null && <div className="text-[10px] text-neutral-400">{t.week} this week</div>
        )}
        {t.note && <div className="text-[10px] text-neutral-400 truncate" data-testid={`launch-health-${t.id}-note`}>{t.note}</div>}
      </>
    );
    const cls = "block rounded-xl border border-neutral-200 bg-white px-3.5 py-3 min-w-0 transition-colors";
    if (t.onClick) {
      return <button type="button" data-testid={`launch-health-${t.id}`} onClick={t.onClick} className={`${cls} text-left w-full hover:border-forest/40 cursor-pointer`}>{inner}</button>;
    }
    if (t.to) {
      return <Link data-testid={`launch-health-${t.id}`} to={t.to} className={`${cls} hover:border-forest/40`}>{inner}</Link>;
    }
    return <div data-testid={`launch-health-${t.id}`} className={cls}>{inner}</div>;
  };

  return (
    <div data-testid="launch-health-panel" className="mt-6 rounded-2xl border border-forest/15 bg-cream/40 p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">
        Launch health <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" />
        <span className="ml-auto font-normal normal-case tracking-normal text-neutral-400">last 24h · auto-refreshes</span>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2">
        {tiles.map((t) => <Tile key={t.id} t={t} />)}
      </div>
      {d.funnel && (() => {
        // volunteer activation funnel (7d): stage → stage conversion
        const f = d.funnel;
        const stages = [
          { id: "signups", label: "Signups", n: f.signups },
          { id: "onboarded", label: "Onboarded", n: f.onboarded },
          { id: "applied", label: "First application", n: f.applied },
          { id: "circuit", label: "Circuit complete", n: f.circuit },
        ];
        const pct = (n, prev) => (prev > 0 ? `${Math.round((n / prev) * 100)}%` : "—");
        return (
          <div data-testid="launch-health-funnel" className="mt-2 rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold mr-1.5">Volunteer funnel · 7d</span>
            {stages.map((s, i) => (
              <React.Fragment key={s.id}>
                {i > 0 && (
                  <span className="text-[10px] text-neutral-400" data-testid={`funnel-pct-${s.id}`}>
                    →&nbsp;{pct(s.n, stages[i - 1].n)}&nbsp;→
                  </span>
                )}
                <span data-testid={`funnel-stage-${s.id}`} className="inline-flex items-baseline gap-1">
                  <span className={`font-display text-lg ${s.n ? "text-forest" : "text-neutral-300"}`}>{s.n}</span>
                  <span className="text-[10px] text-neutral-500">{s.label}</span>
                </span>
              </React.Fragment>
            ))}
          </div>
        );
      })()}
      {allQuiet && (
        <div data-testid="launch-health-quiet" className="mt-3 text-xs text-neutral-500">
          All quiet — counts tick up within a minute once friends &amp; family start arriving.
        </div>
      )}
    </div>
  );
};
